// core/events.js

/*
event naming guide
------------------
- use kebab-case strings with a clear namespace: "namespace:event" or "namespace:sub-event"
- keep nouns on the left (subject) and verbs on the right, e.g. "slides:changed", "track:rest"
- prefer explicit phases when useful: "carousel:before-transition", "carousel:after-transition"
- payloads are plain objects; keys are stable and documented below

payload cheat sheet (subscribe payload shapes)
----------------------------------------------
Carousel
carousel:init                { }
carousel:ready               { }
carousel:reinit              { reason?:string }
carousel:destroy             { }
carousel:error               { message:string, error?:any }
carousel:before-transition   { currentIndex:number, nextIndex:number, kind:'slide'|'page'|'position' }
carousel:after-transition    { previousIndex:number, currentIndex:number, kind:'slide'|'page'|'position' }
carousel:has-focus           { }
carousel:lost-focus          { }

Store
options:changed              { previousOptions, currentOptions }
state:changed                { previousState, currentState }
render-index:changed         { previousIndex, currentIndex }
selected-index:changed       { previousIndex, currentIndex }
page-index:changed           { previousPageIndex, currentPageIndex }
page-count:changed           { count }
can-loop:changed             { canLoop:boolean }
snap-points:changed          { snapPoints, firstSnapPoint, lastSnapPoint }
layout:changed               { previousWidths, currentWidths }
slides:changed               { previousSlides, currentSlides }
transform-points:changed     { previousPoints, currentPoints }
store:changed-dirty          { }
store:changed-clean          { }

Drag
drag:start                   { event, drag }
drag:move                    { event, drag }
drag:end                     { event, drag }
drag:cancel                  { reason? }

Frame / effect render
frame:before-render          { state, widths, slides, animation }
frame:after-render           { state, widths, slides, animation }

Slides
slide:click                  { index, renderIndex, event }
slides:visible-changed       { partiallyVisibleSlides:HTMLElement[], mostlyVisibleSlides:HTMLElement[], fullyVisibleSlides:HTMLElement[], partiallyVisibleIndices:number[], mostlyVisibleIndices:number[], fullyVisibleIndices:number[] }

Window
window:resize                { event? }
window:visibility-change     { hidden:boolean }
window:has-focus             { }
window:lost-focus            { }

Track
track:position-changed       { previousTrackPosition, currentTrackPosition, trackDelta, movementType, trackPercent }
track:shifted                { trackPosition, movementType }
track:request-frame          { time:number } - for animation frame continuation

Effect
effect:changed               { previousEffect?, currentEffect, effectName }
effect:loaded                { effect, effectName }
effect:destroyed             { effectName }

Movement (movementType: 'animate' | 'jump' | 'settle' | 'drag' | 'momentum' | 'scroll')
  'scroll' is used by plugins that drive track position continuously (scrollbar drag, autoscroll)
movement:requested           { index?, pageIndex?, trackPosition?, velocity, movementType }
movement:started             { renderIndex, pageIndex, velocity, movementType }
movement:completed           { renderIndex, pageIndex, movementType }

Engine
engine:position-changed      { position, velocity, progress, delta }
engine:finished              { position, finalVelocity }

User
user:interacted              { via:'button'|'pagination'|'scrollbar'|'drag'|'wheel'|'key'|'focus', event }

Keyboard
keyboard:arrow               { direction:-1|1, event }
*/

export const EVENTS = Object.freeze({
	// lifecycle (component-level)
	carousel: Object.freeze({
		init: 'carousel:init',
		ready: 'carousel:ready',
		reinit: 'carousel:reinit',
		destroy: 'carousel:destroy',
		error: 'carousel:error',
		beforeTransition: 'carousel:before-transition',
		afterTransition: 'carousel:after-transition',
		hasFocus: 'carousel:has-focus',
		lostFocus: 'carousel:lost-focus',
	}),

	// data store slices
	store: Object.freeze({
		optionsChanged: 'options:changed',
		stateChanged: 'state:changed',
		layoutChanged: 'layout:changed',
		slidesChanged: 'slides:changed',
		pageIndexChanged: 'page-index:changed',
		pageCountChanged: 'page-count:changed',
		canLoopChanged: 'can-loop:changed',
		snapPointsChanged: 'snap-points:changed',
		selectedIndexChanged: 'selected-index:changed',
		renderIndexChanged: 'render-index:changed',
		transformPointsChanged: 'transform-points:changed',
		changedDirty: 'store:changed-dirty',
		changedClean: 'store:changed-clean',
	}),

	// user input (raw)
	drag: Object.freeze({
		start: 'drag:start',
		move: 'drag:move',
		end: 'drag:end',
		cancel: 'drag:cancel',
	}),

	// frame + effect pipeline
	frame: Object.freeze({
		beforeRender: 'frame:before-render',
		afterRender: 'frame:after-render',
	}),

	// slides (ui interactions)
	slides: Object.freeze({
		click: 'slides:click',
		visibleChanged: 'slides:visible-changed',
	}),

	// window (host environment)
	window: Object.freeze({
		resize: 'window:resize',
		orientationChange: 'window:orientation-change',
		visibilityChange: 'window:visibility-change',
		hasFocus: 'window:has-focus',
		lostFocus: 'window:lost-focus',
	}),

	// track / loop
	track: Object.freeze({
		looped: 'track:looped',
		shifted: 'track:shifted',
		requestFrame: 'track:request-frame',
		positionChanged: 'track:position-changed',
	}),

	// effect loading and changes
	effect: Object.freeze({
		changed: 'effect:changed',
		loaded: 'effect:loaded',
		destroyed: 'effect:destroyed',
	}),

	// movement lifecycle
	movement: Object.freeze({
		requested: 'movement:requested',
		started: 'movement:started',
		completed: 'movement:completed',
	}),

	// physics engine events
	engine: Object.freeze({
		positionChanged: 'engine:position-changed',
		finished: 'engine:finished',
	}),

	user: Object.freeze({
		interacted: 'user:interacted',
	}),

	keyboard: Object.freeze({
		arrow: 'keyboard:arrow',
	}),
});
